import { Subscription } from 'rxjs';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges, OnInit, Injector, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  NgControl
} from '@angular/forms';
import { AsYouType, CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { StringHelper } from '../../../helpers/string.helper';
import { Country, geoData } from '../../../models/geo.data';
import { LocalStorageService } from '../../../services/local-storage.service';
import { LocLabel, LocLabelModifier } from '../../clickable-row-table/models/data-table-config.models';

@Component({
  selector: 'app-contact-phone-type-form',
  templateUrl: './contact-phone-type-form.component.html',
  styleUrls: ['./contact-phone-type-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContactPhoneTypeFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ContactPhoneTypeFormComponent),
      multi: true
    }
  ]
})
export class ContactPhoneTypeFormComponent implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy {
  @Input() disabled = false;
  @Input() labelKey: string | LocLabel | null = { key: 'label.phone', modifier: LocLabelModifier.CAP };
  @Input() fixedCountry?: string;

  @Input() default!: string;
  @Input() outsideLabel = true;
  /** @deprecated use form validators instead */
  @Input() required = false;
  @Input() footer = true;

  public ngControl!: NgControl;

  public countries: Country[] = [...geoData];

  public get value(): string | null {
    return this._value ? this._value : null;
  }

  public get selectedCountry(): Country {
    return this._selectedCountry;
  }

  public set selectedCountry(value: Country) {
    this._selectedCountry = value;
    this.setValue(true);
    this.saveSelectedCountry(value);
  }

  public get localNumber(): string | null {
    return this._localNumber ? this._localNumber : null;
  }

  public set localNumber(value: string | null) {
    this._localNumber = value ?? '';
    this.setValue(true);
  }

  private readonly PRIORITY_COUNTRIES = ['PL', 'UA', 'BY', 'RU', 'LT', 'LV', 'EE', 'DE', 'AT', 'CH', 'CZ', 'SK'];
  private readonly LAST_CODES_LIMIT = 5;
  private readonly DEFAULT_COUNTRY = geoData.find((x) => x.twoLetterCode == 'PL')!;

  /**
   * twoLetterCode_countryCode because Puerto Rico and Dominican Republic has many country codes
   * and Russia an Kazakhstan has the same one
   */
  private priorityCodes: string[];
  private lastUsedCodes: string[];

  private onChange: any = () => {};
  private onTouch: any = () => {};
  private onValidatorChange: any = () => {};

  private _value = '';
  private _localNumber: string = '';
  private _selectedCountry: Country = this.DEFAULT_COUNTRY;

  private subscription$ = new Subscription();

  constructor(
    private injector: Injector,
    private localStorageSrv: LocalStorageService
  ) {
    this.priorityCodes = this.PRIORITY_COUNTRIES.map((x) => `${x}_${geoData.find((y) => y.twoLetterCode == x)?.countryCode}`);
    this.lastUsedCodes = localStorageSrv.lastUsedCountries.get()?.split('|') ?? [];
    this.sortCountries();
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fixedCountry?.currentValue) {
      const country = this.countries.find((x) => x.twoLetterCode == this.fixedCountry);
      if (country) this.selectedCountry = country;
    }
    if (changes.required) {
      this.onValidatorChange();
    }
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  writeValue(value: any): void {
    let number: string = value?.toString().replace(/\s/g, '') ?? '';
    let country: Country | null = null;

    for (const c of this.countries) {
      if (number.startsWith(`+${c.countryCode}`)) {
        country = c;
        break;
      }
    }

    if (country) {
      number = number.substring(country.countryCode.length + 1); // `+${countryCode}`
    } else {
      country = this.DEFAULT_COUNTRY;
    }
    const formatter = new AsYouType(country.twoLetterCode as CountryCode);

    this._selectedCountry = country;
    this._localNumber = formatter.input(number);

    this.setValue(false);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public changed(): boolean {
    return this.default ? !!StringHelper.getDigits(this._value).localeCompare(StringHelper.getDigits(this.default)) : false;
  }

  public reset(): void {
    this.writeValue(this.default);
  }

  public showCountryDivider(index: number): boolean {
    return index == this.priorityCodes.length - 1 || index == this.priorityCodes.length + this.lastUsedCodes.length - 1;
  }

  private setValue(emitEvent: boolean): void {
    this._value = this.localNumber ? `+${this._selectedCountry.countryCode} ${this.localNumber}` : '';

    if (emitEvent) {
      this.onChange(this.value);
      this.onTouch();
      this.onValidatorChange();

      if (this.ngControl?.control) {
        this.ngControl.control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public validate(control: AbstractControl): ValidationErrors | null {
    let errors = null;

    switch (this._localNumber.length) {
      case 0:
        errors = this.required ? { required: true } : null;
        break;
      case 3: // allow emergency numbers
        errors = null;
        break;
      default:
        try {
          const phoneNumber = parsePhoneNumberWithError(this._value);
          if (!phoneNumber.isValid()) errors = { phoneNumber: true };
        } catch (e: any) {
          if (e.message != 'NOT_A_NUMBER' && e.message != 'TOO_SHORT' && e.message != 'TOO_LONG') throw e;
        }
        break;
    }
    return errors;
  }

  private saveSelectedCountry(value: Country): void {
    const key = `${value.twoLetterCode}_${value.countryCode}`;
    if (!this.priorityCodes.includes(key) && !this.lastUsedCodes.includes(key)) {
      this.lastUsedCodes = [key, ...this.lastUsedCodes.slice(0, this.LAST_CODES_LIMIT - 1)];
      this.localStorageSrv.lastUsedCountries.set(this.lastUsedCodes.join('|'));
      this.sortCountries();
    }
  }

  private sortCountries(): void {
    this.countries.sort((x, y) => {
      const priority = [...this.priorityCodes, ...this.lastUsedCodes];
      const index1 = priority.indexOf(`${x.twoLetterCode}_${x.countryCode}`);
      const index2 = priority.indexOf(`${y.twoLetterCode}_${y.countryCode}`);
      if (index1 < 0 && index2 >= 0) return 1;
      if (index1 >= 0 && index2 < 0) return -1;
      return index1 - index2;
    });
  }
}
